/**
 * Podpowiedź, który operator sieci (OSD) obsługuje adres klienta.
 *
 * Pewność daje tylko rachunek za prąd albo kod PPE (prefiks firmy w kodzie GS1). Kod pocztowy
 * i województwo to przybliżenie: granice obszarów OSD nie pokrywają się z granicami województw
 * ani rejonów pocztowych, więc pokazujemy „najpewniej” i drugiego operatora, gdy dzielą teren.
 * Źródła i data sprawdzenia: `VV/dane_badawcze/osd_zgloszenia/obszary_osd_2026-10-07.md`.
 */

/** Operatorzy w województwie: pierwszy obsługuje większość terenu. */
export const OSD_WOJEWODZTW = {
  dolnoslaskie: ['tauron'],
  'kujawsko-pomorskie': ['enea', 'energa'],
  lubelskie: ['pge'],
  lubuskie: ['enea'],
  lodzkie: ['pge', 'energa', 'tauron'], // Energa: Kutno, Łęczyca, Wieruszów; Tauron: Radomsko, Pajęczno
  mazowieckie: ['pge', 'stoen', 'energa'],
  malopolskie: ['tauron'],
  opolskie: ['tauron'],
  podkarpackie: ['pge', 'tauron'], // zachodni skraj (dawne tarnowskie: Dębica, Mielec) w Tauronie
  podlaskie: ['pge'],
  pomorskie: ['energa', 'enea'], // Enea: Chojnice, Człuchów (gminy dzielone)
  slaskie: ['tauron'],
  swietokrzyskie: ['pge', 'tauron'], // Tauron: Włoszczowa, Kazimierza Wielka
  'warminsko-mazurskie': ['energa', 'pge'], // wschód (dawne suwalskie: Ełk, Gołdap, Olecko) w PGE
  wielkopolskie: ['enea', 'energa'],
  zachodniopomorskie: ['enea', 'energa'],
}

/** Same cyfry z wpisu użytkownika. */
const cyfry = (t) => String(t || '').replace(/\D/g, '')

/**
 * Wpis (kod pocztowy albo PPE) -> { klucz, pewnosc: 'pewne' | 'prawdopodobne', powod, inni } albo null.
 * PPE rozpoznajemy po 18 cyfrach zaczynających się od 590, kod pocztowy po formacie 00-000.
 */
export const podpowiedzOsd = (wpis) => {
  const t = String(wpis || '').trim()
  const c = cyfry(t)
  // Kody sprzed renumeracji 2021–2023 zaczynały się od „PL” i nie niosą numeru operatora w GS1.
  if (/^PL/i.test(t.replace(/\s/g, '')) && t.length >= 10) {
    return { klucz: null, komunikat: 'To stary kod PPE sprzed zmiany numeracji. Nowy, 18-cyfrowy kod jest na aktualnym rachunku.' }
  }
  if (c.length === 18 && c.startsWith('590')) {
    const klucz = osdZPpe(c)
    if (klucz) return { klucz, pewnosc: 'pewne', powod: 'kod PPE wskazuje operatora', inni: [] }
    const maly = MALI_OSD_PPE.find(([prefiks]) => c.startsWith(prefiks))
    return { klucz: null, komunikat: maly
      ? `Ten kod PPE należy do operatora ${maly[1]}, którego wzoru nie mamy. Dane sprzętu poniżej przydadzą się mimo to.`
      : 'Nie rozpoznajemy operatora po tym kodzie PPE. Nazwa operatora jest na rachunku.' }
  }
  const kod = t.match(/^(\d{2})-?(\d{3})$/)
  if (kod) return osdZKoduPocztowego(kod[1], kod[2])
  return null
}

/**
 * Prefiksy firm w kodach PPE: 590 (GS1 Polska) + 4 cyfry numeru operatora, potem numer punktu i cyfra
 * kontrolna. Siedem cyfr wystarcza dla dużej piątki (źródła w pliku z danymi badawczymi w nagłówku).
 */
export const PREFIKSY_PPE = [
  ['5903106', 'enea'],
  ['5903224', 'tauron'],
  ['5902438', 'energa'], // nowa numeracja od 15.04.2021
  ['5905435', 'pge'], // od 26.06.2023
  ['5903801', 'stoen'], // od 20.11.2022
]

/** Mniejsi operatorzy, których rozpoznajemy tylko po to, żeby nie zgadywać dużego. */
const MALI_OSD_PPE = [['5905088', 'PGE Energetyka Kolejowa']]

export const osdZPpe = (ppe) => {
  const c = cyfry(ppe)
  const trafienie = PREFIKSY_PPE.find(([prefiks]) => c.startsWith(prefiks))
  return trafienie ? trafienie[1] : null
}

/**
 * Rejony pocztowe (dwie pierwsze cyfry) -> operatorzy, pierwszy najpewniejszy. [hipoteza] Przypisanie z wiedzy
 * o miastach rejonów i oddziałach OSD (zgodnych z województwami z lat 1975–98), niesprawdzone na punktach
 * adresowych PRG; dlatego zawsze „najpewniej”, a przy kilku operatorach pokazujemy pozostałych.
 */
const REJONY = {
  stoen: ['00', '01', '02', '03', '04'],
  pge: ['05', '07', '08', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '35', '36', '37',
    '90', '91', '92', '93', '94', '95', '96'],
  energa: ['09', '10', '13', '14', '75', '76', '80', '81', '82', '83', '84', '87'],
  enea: ['60', '61', '64', '65', '66', '68', '69', '70', '71', '72', '73', '74', '85', '89'],
  tauron: ['30', '31', '32', '33', '34', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '51', '52', '53',
    '54', '55', '57', '58', '59'],
}
const REJONY_WSPOLNE = {
  '06': ['energa', 'pge'], '11': ['energa', 'pge'], '12': ['energa', 'pge'], '28': ['pge', 'tauron'], '29': ['pge', 'tauron'],
  '38': ['pge', 'tauron'], '39': ['tauron', 'pge'], '56': ['tauron', 'energa', 'enea'], '62': ['energa', 'enea'],
  '63': ['energa', 'enea', 'tauron'], '67': ['enea', 'tauron'], '77': ['energa', 'enea'], '78': ['energa', 'enea'],
  '86': ['enea', 'energa'], '88': ['enea', 'energa'], '97': ['pge', 'tauron'], '98': ['pge', 'energa', 'tauron'],
  '99': ['energa', 'pge'],
}
export const OSD_REJONOW_POCZTOWYCH = {
  ...Object.fromEntries(Object.entries(REJONY).flatMap(([klucz, rejony]) => rejony.map((r) => [r, [klucz]]))),
  ...REJONY_WSPOLNE,
}

export const osdZKoduPocztowego = (rejon) => {
  const lista = OSD_REJONOW_POCZTOWYCH[rejon]
  if (!lista?.length) return null
  return { klucz: lista[0], pewnosc: 'prawdopodobne', powod: `rejon pocztowy ${rejon}`, inni: lista.slice(1) }
}

/**
 * Klucz województwa mapy (jak `OSD_WOJEWODZTW`, `regionyPolski.ts`) z nazwy z karty klienta:
 * „łódzkie”, „Łódzkie”, „woj. łódzkie”, „Kujawsko - Pomorskie” → klucz; nieznana nazwa → null.
 */
export const kluczWojewodztwaMapy = (nazwa) => {
  const t = String(nazwa || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ł/g, 'l')
    .replace(/^\s*(wojewodztwo|woj\.?)\s*/, '').replace(/\s*-\s*/g, '-').replace(/\s+/g, '-').trim()
  return Object.prototype.hasOwnProperty.call(OSD_WOJEWODZTW, t) ? t : null
}
